import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { neoApi, type NeoTask, type NeoOnbTemplate, type ApiError } from '../api';
import { Page, Card, Field, Input, Textarea, Button, IconRocket, IconPlus } from '../ui';

/**
 * Novo projeto (NEO Pilots) — onboarding por TIPO de projeto. O usuário escolhe o
 * fluxo (marketing, só a ideia, site, material pronto); cada um tem seu formulário.
 * Dá pra anexar documentos desde o início. Depois dispara o bloco Fundação no Hermes.
 */

interface Question {
  questionKey: string;
  question: string;
  hint?: string;
}
interface Template {
  id: string;
  icon: string;
  name: string;
  desc: string;
  required: number;
  questions: Question[];
}

const TEMPLATES: Template[] = [
  {
    id: 'marketing',
    icon: '📊',
    name: 'Estratégia de marketing',
    desc: 'Do zero: diagnóstico, posicionamento, plano de crescimento e identidade da marca.',
    required: 4,
    questions: [
      { questionKey: 'marca', question: 'O que é a marca ou produto?', hint: 'Em uma ou duas frases.' },
      { questionKey: 'publico', question: 'Quem é o público?', hint: 'Pra quem você vende ou quer falar.' },
      { questionKey: 'objetivo', question: 'Qual o principal objetivo agora?', hint: 'Ex: mais vendas, reconhecimento, lançar algo.' },
      { questionKey: 'concorrentes', question: 'Concorrentes ou referências?', hint: 'Marcas parecidas ou que você admira.' },
      { questionKey: 'restricoes', question: 'Alguma restrição?', hint: 'Orçamento, prazo, algo a evitar. (opcional)' },
    ],
  },
  {
    id: 'ideia',
    icon: '💡',
    name: 'Só tenho a ideia',
    desc: 'Me conta a ideia e a IA estrutura todo o planejamento a partir dela.',
    required: 2,
    questions: [
      { questionKey: 'ideia', question: 'Descreva a sua ideia', hint: 'O que você quer criar, em poucas linhas.' },
      { questionKey: 'objetivo', question: 'O que você quer alcançar?', hint: 'Ex: vender, ter audiência, testar o conceito.' },
    ],
  },
  {
    id: 'site',
    icon: '🌐',
    name: 'Quero um site / blog',
    desc: 'Vamos criar e publicar seu site na nossa estrutura.',
    required: 2,
    questions: [
      { questionKey: 'sobre', question: 'Sobre o que é o site?', hint: 'O tema, o negócio, o conteúdo.' },
      { questionKey: 'publico', question: 'Pra quem é?', hint: 'Quem vai visitar.' },
      { questionKey: 'estilo', question: 'Referências ou estilo visual?', hint: 'Sites que você gosta, cores, tom. (opcional)' },
      { questionKey: 'objetivo', question: 'Qual o objetivo do site?', hint: 'Ex: vender, informar, captar contatos.' },
    ],
  },
  {
    id: 'material',
    icon: '📎',
    name: 'Já tenho material',
    desc: 'Suba o que já tem (briefing, documentos) e a gente continua a partir dali.',
    required: 1,
    questions: [
      { questionKey: 'contexto', question: 'O que você já tem e o que precisa?', hint: 'Um resumo rápido; anexe os arquivos abaixo.' },
    ],
  },
];

const STEPS = ['Fluxo', 'Nome', 'Sobre o projeto', 'Planejamento'];

const SKILL_LABELS: Record<string, string> = {
  '00-diagnostico': 'Diagnóstico',
  '01-posicionamento': 'Posicionamento',
  '02-growth': 'Plano de crescimento',
  '04-brandguide': 'Identidade da marca',
};

const STATUS_UI: Record<NeoTask['status'], { label: string; color: string; bg: string }> = {
  blocked: { label: 'Aguardando', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  todo: { label: 'Na fila', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  running: { label: 'Gerando…', color: '#d97706', bg: 'rgba(251,191,36,0.15)' },
  review: { label: 'Em revisão', color: '#7c3aed', bg: 'rgba(167,139,250,0.15)' },
  done: { label: 'Pronto', color: '#059669', bg: 'rgba(52,211,153,0.15)' },
  rollback: { label: 'Falhou', color: '#dc2626', bg: 'rgba(251,113,133,0.15)' },
};

function Stepper({ current }: { current: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-5)', flexWrap: 'wrap' }}>
      {STEPS.map((label, i) => {
        const active = i === current;
        const done = i < current;
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <div
              style={{
                width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center',
                fontSize: 'var(--font-size-xs)', fontWeight: 600,
                background: active || done ? 'var(--color-accent)' : 'var(--color-bg-subtle)',
                color: active || done ? '#fff' : 'var(--color-fg-muted)',
              }}
            >
              {done ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: 'var(--font-size-sm)', color: active ? 'var(--color-fg)' : 'var(--color-fg-muted)', fontWeight: active ? 600 : 400 }}>
              {label}
            </span>
            {i < STEPS.length - 1 && <span style={{ width: 24, height: 1, background: 'var(--color-border)' }} />}
          </div>
        );
      })}
    </div>
  );
}

export function NeoOnboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState<NeoOnbTemplate[]>(TEMPLATES);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [tasks, setTasks] = useState<NeoTask[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const template = templates.find((t) => t.id === templateId) ?? null;

  useEffect(() => {
    neoApi.onboardingTemplates().then((r) => { if (r.templates.length) setTemplates(r.templates); }).catch(() => undefined);
  }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  async function createProject(draft: boolean) {
    setError(null);
    if (!draft && !name.trim()) { setError('Dê um nome ao projeto.'); return; }
    setBusy(true);
    try {
      const r = await neoApi.createProject(draft ? 'Novo Projeto' : name.trim());
      setSlug(r.project.slug);
      setStep(2);
    } catch (e) {
      setError(`Não consegui criar: ${(e as ApiError).error}`);
    } finally {
      setBusy(false);
    }
  }

  function poll() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await neoApi.kanban(slug);
        setTasks(r.tasks);
        const settled = r.tasks.length > 0 && r.tasks.every((t) => t.status === 'done' || t.status === 'rollback');
        if (settled && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } catch { /* tenta de novo no próximo tick */ }
    }, 3000);
  }

  async function startFundacao() {
    if (!template) return;
    setError(null);
    setBusy(true);
    try {
      if (files.length) await neoApi.uploadDocs(slug, files).catch(() => undefined);
      const responses = [
        { questionKey: '_fluxo', question: 'Tipo de projeto', answer: template.name, order: 0 },
        ...template.questions.map((q, i) => ({ questionKey: q.questionKey, question: q.question, answer: answers[q.questionKey] ?? '', order: i + 1 })),
      ];
      await neoApi.saveOnboarding(slug, responses);
      await neoApi.runFundacao(slug);
      setStep(3);
      const r = await neoApi.kanban(slug);
      setTasks(r.tasks);
      poll();
    } catch (e) {
      setError(`Não consegui iniciar: ${(e as ApiError).error}`);
    } finally {
      setBusy(false);
    }
  }

  const allDone = tasks.length > 0 && tasks.every((t) => t.status === 'done');
  const requiredAnswered = !!template && template.questions.slice(0, template.required).every((q) => (answers[q.questionKey] ?? '').trim().length > 0);

  return (
    <Page title="Novo projeto" subtitle="Escolha por onde começar. A equipe de IA monta a base do seu projeto a partir daí.">
      <div style={{ maxWidth: 680 }}>
        <Stepper current={step} />

        {error && <div className="ui-error-banner" style={{ marginBottom: 'var(--space-4)' }}>{error}</div>}

        {/* Passo 0 — escolher o tipo de projeto */}
        {step === 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-3)' }}>
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => { setTemplateId(t.id); setAnswers({}); setStep(1); }}
                style={{
                  textAlign: 'left', cursor: 'pointer', padding: 'var(--space-4)',
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg, 12px)',
                  background: 'var(--color-bg)', color: 'var(--color-fg)',
                }}
              >
                <div style={{ fontSize: 26, marginBottom: 'var(--space-2)' }}>{t.icon}</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.name}</div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)' }}>{t.desc}</div>
              </button>
            ))}
          </div>
        )}

        {/* Passo 1 — nome */}
        {step === 1 && (
          <Card>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)', marginBottom: 'var(--space-3)' }}>
              {template?.icon} {template?.name}
            </div>
            <Field label="Nome do projeto">
              <Input
                autoFocus
                placeholder="Ex: Mistik Island"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void createProject(false); }}
              />
            </Field>
            <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="primary" leftIcon={<IconPlus size={14} />} disabled={busy} onClick={() => void createProject(false)}>
                {busy ? 'Criando…' : 'Continuar'}
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => void createProject(true)}>Decidir depois</Button>
              <Button variant="ghost" disabled={busy} onClick={() => setStep(0)}>← Trocar tipo</Button>
            </div>
            <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-fg-muted)' }}>
              Sem nome ainda? Responda sobre o projeto e construímos um nome depois.
            </div>
          </Card>
        )}

        {/* Passo 2 — formulário do fluxo + upload */}
        {step === 2 && template && (
          <Card>
            <div style={{ marginBottom: 'var(--space-4)', color: 'var(--color-fg-muted)', fontSize: 'var(--font-size-sm)' }}>
              {template.icon} {template.name} — quanto mais contexto, melhor o resultado.
            </div>
            {template.questions.map((q) => (
              <div key={q.questionKey} style={{ marginBottom: 'var(--space-4)' }}>
                <Field label={q.question} hint={q.hint}>
                  <Textarea rows={2} value={answers[q.questionKey] ?? ''} onChange={(e) => setAnswers((a) => ({ ...a, [q.questionKey]: e.target.value }))} />
                </Field>
              </div>
            ))}

            {/* upload de documentos (opcional em qualquer fluxo) */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Field label="Documentos (opcional)" hint="Briefing, PDFs, referências — o que você já tiver.">
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                  style={{ fontSize: 'var(--font-size-sm)' }}
                />
              </Field>
              {files.length > 0 && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-fg-muted)', marginTop: 4 }}>
                  {files.length} arquivo(s): {files.map((f) => f.name).join(', ')}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="primary" leftIcon={<IconRocket size={14} />} disabled={busy || !requiredAnswered} onClick={() => void startFundacao()}>
                {busy ? 'Iniciando…' : 'Gerar planejamento'}
              </Button>
              {!requiredAnswered && (
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)' }}>
                  Responda as {template.required} primeira(s) pra continuar.
                </span>
              )}
            </div>
          </Card>
        )}

        {/* Passo 3 — planejamento */}
        {step === 3 && (
          <Card>
            <div style={{ marginBottom: 'var(--space-4)', color: 'var(--color-fg-muted)', fontSize: 'var(--font-size-sm)' }}>
              {allDone ? 'Pronto! Seu planejamento inicial está montado.' : 'A equipe está montando seus documentos. Pode levar alguns minutos.'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
              {tasks.map((t) => {
                const ui = STATUS_UI[t.status];
                return (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)' }}>
                    <span style={{ fontWeight: 500 }}>{SKILL_LABELS[t.skillSlug] ?? t.skillSlug}</span>
                    <span style={{ color: ui.color, background: ui.bg, padding: '2px 10px', borderRadius: 999, fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{ui.label}</span>
                  </div>
                );
              })}
            </div>
            {allDone && (
              <Button variant="primary" onClick={() => nav(`/p/${slug}/plano`)}>Ver o Plano do Projeto →</Button>
            )}
          </Card>
        )}
      </div>
    </Page>
  );
}
